import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@core/services/api.service';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { RoleConfig } from '@core/models/role-config.model';

@Component({
  standalone: false,
  selector: 'app-role-management',
  templateUrl: './role-management.component.html',
  styleUrls: ['./role-management.component.scss']
})
export class RoleManagementComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  roles: any[] = [];
  employees: any[] = [];
  loading = true;
  activeTab = 'roles';

  showDialog = false;
  showSeedDialog = false;
  editingRole: any = null;
  submitting = false;
  seedingRoles = false;

  showRoleDrawer = false;
  drawerRole: any = null;
  drawerModuleList: any[] = [];

  get totalRoleCount() { return this.allRolesFlat.length; }
  get systemRoleCount() { return this.allRolesFlat.filter(r => r.isBuiltIn || r.isSystem).length; }
  get totalUserCount() { return this.employees.length; }
  get adminCount() { return this.employees.filter(e => ['admin','super_admin','hr','hr_admin'].includes((e.role||'').toLowerCase().replace(/^role_/,''))).length; }
  get managerCount() { return this.employees.filter(e => (e.role||'').toLowerCase().replace(/^role_/,'') === 'manager').length; }
  get hrCount() { return this.employees.filter(e => ['hr','hr_admin'].includes((e.role||'').toLowerCase().replace(/^role_/,''))).length; }
  get employeeCount() { return this.employees.filter(e => (e.role||'').toLowerCase().replace(/^role_/,'') === 'employee').length; }

  get allRolesFlat(): any[] {
    return this.roles.map(r => ({
      ...r,
      role: r.name,
      menuCount: this.getMenuCount(r),
      moduleCount: this.getOpModuleCount(r),
      menuKeys: this.parseJson(r.menuPermissions, []),
      modulePermissions: this.parseJson(r.operationPermissions, {}),
      allowedRoutes: this.parseJson(r.allowedRoutes, []),
      isBuiltIn: false,
      isSystem: !!r.isSystem
    }));
  }

  allMenuItems = [
    { key: 'dashboard',        label: 'Dashboard',        icon: 'dashboard',            group: 'Core' },
    { key: 'profile',          label: 'My Profile',       icon: 'account_circle',       group: 'Core' },
    { key: 'employees',        label: 'Employees',        icon: 'people',               group: 'People' },
    { key: 'departments',      label: 'Departments',      icon: 'business',             group: 'People' },
    { key: 'teams',            label: 'Teams',            icon: 'group_work',           group: 'People' },
    { key: 'my_team',          label: 'My Team',          icon: 'supervisor_account',   group: 'People' },
    { key: 'attendance',       label: 'Attendance',       icon: 'schedule',             group: 'Time' },
    { key: 'leave',            label: 'Leave',            icon: 'event_busy',           group: 'Time' },
    { key: 'overtime',         label: 'Overtime',         icon: 'more_time',            group: 'Time' },
    { key: 'timesheet',        label: 'Timesheets',       icon: 'pending_actions',      group: 'Time' },
    { key: 'holidays',         label: 'Holidays',         icon: 'celebration',          group: 'Time' },
    { key: 'shifts',           label: 'Shifts',           icon: 'access_time',          group: 'Time' },
    { key: 'roster',           label: 'Roster',           icon: 'event_note',           group: 'Time' },
    { key: 'change_requests',  label: 'Roster Requests',  icon: 'published_with_changes', group: 'Time' },
    { key: 'payroll',          label: 'Payroll',          icon: 'payments',             group: 'Finance' },
    { key: 'my_payslips',      label: 'My Payslips',      icon: 'receipt',              group: 'Finance' },
    { key: 'performance',      label: 'Performance',      icon: 'trending_up',          group: 'Workforce' },
    { key: 'onboarding',       label: 'Onboarding',       icon: 'person_add',           group: 'Workforce' },
    { key: 'offboarding',      label: 'Offboarding',      icon: 'person_remove',        group: 'Workforce' },
    { key: 'asset_management', label: 'Asset Management', icon: 'devices',              group: 'Workforce' },
    { key: 'clients',          label: 'Clients',          icon: 'handshake',            group: 'Business' },
    { key: 'announcements',    label: 'Announcements',    icon: 'campaign',             group: 'Business' },
    { key: 'reports',          label: 'Reports',          icon: 'assessment',           group: 'Business' },
    { key: 'geo_zones',        label: 'Geo Zones',        icon: 'location_on',          group: 'Admin' },
    { key: 'email_templates',  label: 'Email Templates',  icon: 'mail',                 group: 'Admin' },
    { key: 'settings',         label: 'Settings',         icon: 'settings',             group: 'Admin' },
    { key: 'roles',            label: 'Roles & Permissions', icon: 'admin_panel_settings', group: 'Admin' },
    { key: 'policies',         label: 'Policies',         icon: 'policy',               group: 'Admin' },
  ];

  operationModules = [
    'employees', 'departments', 'attendance', 'leave', 'teams', 'shifts', 'roster',
    'announcements', 'payroll', 'reports', 'settings', 'roles', 'timesheet',
    'onboarding', 'offboarding', 'performance', 'overtime', 'holidays',
    'asset_management', 'geo_zones', 'my_payslips', 'change_requests',
    'clients', 'email_templates'
  ];

  scopeOptions = ['self', 'team', 'department', 'all'];

  form: any = {
    name: '', displayName: '', description: '',
    menuPermissions: [] as string[],
    operationPermissions: {} as any,
    allowedRoutes: [] as string[]
  };

  constructor(
    private apiService: ApiService,
    private headerService: HeaderService,
    private snackbarService: SnackbarService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Roles & Permissions');
    forkJoin({
      roles: this.apiService.get('/roles').pipe(catchError(() => of([]))),
      employees: this.apiService.get('/employees').pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.roles = this.extractList(data.roles);
        this.employees = this.extractList(data.employees);
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  private extractList(r: any): any[] {
    if (Array.isArray(r)) return r;
    if (Array.isArray(r?.data)) return r.data;
    return [];
  }

  private parseJson(val: any, fallback: any): any {
    if (!val) return fallback;
    try { return JSON.parse(val); } catch { return fallback; }
  }

  openRoleDrawer(r: any): void {
    this.drawerRole = r;
    const perms = r.modulePermissions || {};
    this.drawerModuleList = this.operationModules
      .filter(m => perms[m])
      .map(m => ({
        label: m.replace(/_/g, ' '),
        canView:   perms[m]?.canView   || perms[m]?.read   || false,
        canAdd:    perms[m]?.canAdd    || perms[m]?.create  || false,
        canEdit:   perms[m]?.canEdit   || perms[m]?.update  || false,
        canDelete: perms[m]?.canDelete || perms[m]?.delete  || false,
        scope:     perms[m]?.scope     || 'self'
      }));
    this.showRoleDrawer = true;
  }

  openEditDialogFor(r: any): void {
    if (r.isBuiltIn) {
      this.snackbarService.showFailure('Built-in roles cannot be edited here. Use the DB-synced version.');
      return;
    }
    this.openEditDialog(r);
  }

  getRoleColor(role: string): string {
    const map: any = { super_admin: 'red', admin: 'red', hr_admin: 'amber', hr: 'amber', manager: 'green', employee: 'blue' };
    return map[role] || 'red';
  }

  getRoleIcon(role: string): string {
    const map: any = { super_admin: 'security', admin: 'admin_panel_settings', hr_admin: 'manage_accounts', hr: 'groups', manager: 'supervisor_account', employee: 'person' };
    return map[role] || 'admin_panel_settings';
  }

  getDefaultDesc(role: string): string {
    const map: any = {
      super_admin: 'Unrestricted access to entire system',
      admin: 'Full system access',
      hr_admin: 'Senior HR with administrative rights',
      hr: 'HR operations and employee management',
      manager: 'Team management and approvals',
      employee: 'Basic employee self-service access'
    };
    return map[role] || 'Custom role';
  }

  get menuGroups(): string[] {
    return [...new Set(this.allMenuItems.map(m => m.group))];
  }

  menuItemsByGroup(grp: string): any[] {
    return this.allMenuItems.filter(m => m.group === grp);
  }

  getMenuLabel(key: string): string {
    return this.allMenuItems.find(m => m.key === key)?.label || key;
  }

  getRoleBadgeClass(role: string): string {
    const r = (role || '').toLowerCase().replace(/^role_/, '');
    if (['admin', 'super_admin'].includes(r)) return 'brr';
    if (['hr', 'hr_admin'].includes(r)) return 'bbl';
    if (r === 'manager') return 'bgr';
    return 'bd';
  }

  seedDefaultRoles(): void {
    this.snackbarService.showFailure('Use the SQL seed script to initialise default roles in the database.');
    this.showSeedDialog = false;
  }

  loadRoles(): void {
    this.apiService.get('/roles').pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.roles = this.extractList(data);
    });
  }

  convertToOperationPermissions(modulePerms: any): any {
    const result: any = {};
    for (const [module, perms] of Object.entries(modulePerms)) {
      const p = perms as any;
      result[module] = { canView: p.canView||false, canAdd: p.canAdd||false, canEdit: p.canEdit||false, canDelete: p.canDelete||false, scope: p.scope||'self' };
    }
    return result;
  }

  openSeedDialog(): void { this.showSeedDialog = true; }
  closeSeedDialog(): void { this.showSeedDialog = false; }

  openCreateDialog(): void {
    this.editingRole = null;
    this.form = { name: '', displayName: '', description: '', menuPermissions: ['dashboard', 'profile'], operationPermissions: {}, allowedRoutes: ['/overview', '/profile'] };
    this.operationModules.forEach(m => {
      this.form.operationPermissions[m] = { canView: false, canAdd: false, canEdit: false, canDelete: false, scope: 'self' };
    });
    this.showDialog = true;
  }

  openEditDialog(role: any): void {
    this.editingRole = role;
    this.form = {
      name: role.name, displayName: role.displayName || role.name, description: role.description || '',
      menuPermissions: [...this.parseJson(role.menuPermissions, [])],
      operationPermissions: {},
      allowedRoutes: this.parseJson(role.allowedRoutes, [])
    };
    const opPerms = this.parseJson(role.operationPermissions, {});
    this.operationModules.forEach(m => {
      const p = opPerms[m] || {};
      this.form.operationPermissions[m] = {
        canView:   p.canView   ?? p.read   ?? false,
        canAdd:    p.canAdd    ?? p.create  ?? false,
        canEdit:   p.canEdit   ?? p.update  ?? false,
        canDelete: p.canDelete ?? p.delete  ?? false,
        scope:     p.scope ?? 'self'
      };
    });
    this.showDialog = true;
  }

  closeDialog(): void { this.showDialog = false; this.editingRole = null; }

  isMenuSelected(key: string): boolean { return this.form.menuPermissions.includes(key); }
  toggleMenu(key: string): void {
    const idx = this.form.menuPermissions.indexOf(key);
    if (idx >= 0) this.form.menuPermissions.splice(idx, 1);
    else this.form.menuPermissions.push(key);
  }
  selectAllMenus(): void { this.form.menuPermissions = this.allMenuItems.map(m => m.key); }
  clearAllMenus(): void { this.form.menuPermissions = []; }

  getMenuCount(role: any): number { return this.parseJson(role.menuPermissions, []).length; }
  getOpModuleCount(role: any): number { return Object.keys(this.parseJson(role.operationPermissions, {})).length; }

  toggleAllOps(mod: string, val: boolean): void {
    const scope = this.form.operationPermissions[mod]?.scope || 'self';
    this.form.operationPermissions[mod] = { canView: val, canAdd: val, canEdit: val, canDelete: val, scope };
  }

  submitRole(): void {
    if (!this.form.name || !this.form.displayName) { this.snackbarService.showFailure('Name and Display Name are required'); return; }
    this.submitting = true;
    const payload = {
      name: this.form.name.toLowerCase().replace(/\s+/g, '_'),
      displayName: this.form.displayName,
      description: this.form.description,
      menuPermissions: JSON.stringify(this.form.menuPermissions),
      operationPermissions: JSON.stringify(this.form.operationPermissions),
      allowedRoutes: JSON.stringify(this.form.allowedRoutes),
      status: 'active'
    };
    const req = this.editingRole ? this.apiService.put('/roles/' + this.editingRole.id, payload) : this.apiService.post('/roles', payload);
    req.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.snackbarService.showSuccess(this.editingRole ? 'Role updated' : 'Role created'); this.closeDialog(); this.loadRoles(); this.submitting = false; },
      error: () => { this.snackbarService.showFailure('Failed to save role'); this.submitting = false; }
    });
  }

  deleteRole(role: any): void {
    if (role.isSystem) { this.snackbarService.showFailure('Cannot delete system role'); return; }
    this.confirmDialog.confirm(`"${role.displayName || role.name}" will be permanently deleted. Employees with this role may lose access.`, {
      title: 'Delete Role', confirmLabel: 'Delete', danger: true
    }).then(ok => {
      if (!ok) return;
      this.apiService.delete('/roles/' + role.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.snackbarService.showSuccess('Role deleted'); this.loadRoles(); },
        error: () => this.snackbarService.showFailure('Failed to delete role')
      });
    });
  }

  getMenuLabels(role: any): string {
    const keys: string[] = this.parseJson(role.menuPermissions, []);
    return keys.slice(0, 4).map(k => this.allMenuItems.find(m => m.key === k)?.label || k).join(', ') + (keys.length > 4 ? ` +${keys.length - 4} more` : '');
  }
}
